import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Button,
    Flex,
    Heading1,
    Heading2,
    IconButton,
    IconControlledSubstance,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import { useIsMobile } from '@myphx-engineering/phx-design-system/hooks';

import { RxForUser } from '../../../components/cabinet/rx-for-user/RxForUser';
import { PrescriptionCancelledAlert } from '../../../components/common/PrescriptionCancelledAlert';
import { Prescriber } from '../../../components/prescriber/Prescriber';
import { PriorAuthLastActivityCard } from '../../../components/prior-authorization/components/PriorAuthLastActivityCard';
import { ConditionalComponent } from '../../../components/utility/ConditionalComponent';
import { getFragment } from '../../../graphql';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    PrescriberOrderStatus,
    type PriceQuoteInfoFragment,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { usePriorAuthorizationDetails } from '../../../hooks/use-prior-authorization-details';
import { getAbsoluteRoute } from '../../../util';

import { LastOrderActivity } from './LastOrderActivity';
import { PrescriberOrderDetails } from './PrescriberOrderDetails';

type OrderDetailsProps = {
    priceQuote?: PriceQuoteInfoFragment;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

export const OrderDetails = ({
    priceQuote,
    prescriberOrder,
}: OrderDetailsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const { setBackOverrideState } = useNavBar();
    const { helpCenterMedicationEnabled } = useFlags();

    useEffect(() => {
        if (prescriberOrder.latestSelectedPriceQuote) {
            setBackOverrideState({
                overrideFn: () => {
                    navigate(getAbsoluteRoute('cabinet.history'));
                },
            });
        }

        return () => setBackOverrideState({ overrideFn: null });
    }, [
        prescriberOrder.latestSelectedPriceQuote,
        navigate,
        setBackOverrideState,
    ]);

    const pharmacyProvider = getFragment(
        getFragment(prescriberOrder.latestSelectedOffer)?.pharmacyProvider
    );
    const prescriptionId = prescriberOrder.drxId ?? prescriberOrder.id;
    const rxPatient = getFragment(prescriberOrder?.patient);
    const controlledSubstanceCode = getFragment(
        prescriberOrder.authorizedMedication
    ).controlledSubstanceCode;

    // Temporary until VELA integration
    const isInstantRouting = false;

    const shouldShowOrderDetails =
        !controlledSubstanceCode ||
        isInstantRouting ||
        !!(controlledSubstanceCode && !prescriberOrder.latestSelectedOffer);

    const applicablePriorAuthorization = getFragment(
        prescriberOrder.applicablePriorAuthorization
    );

    const { isApprovedPriorAuthorization } = usePriorAuthorizationDetails(
        applicablePriorAuthorization
    );

    // Note: Allow Fill when it's an approved non-specialty dRx PA or just new dRx
    const showFillButton =
        (isApprovedPriorAuthorization &&
            !applicablePriorAuthorization?.isSpecialty &&
            prescriberOrder.status !== PrescriberOrderStatus.SentToPharmacy) ||
        prescriberOrder.status === PrescriberOrderStatus.New;

    return (
        <Stack gap="lg" flex="1">
            <Stack gap="xl" flex={isMobile ? '1' : '0'}>
                <Stack gap="lg">
                    <PrescriptionCancelledAlert
                        prescriberOrder={prescriberOrder}
                    />
                    <Stack gap="xs">
                        <RxForUser patient={rxPatient} />
                        <Flex align="center" gap="xxs">
                            <Heading1>
                                {prescriberOrder.productDescription}
                            </Heading1>
                            {controlledSubstanceCode ? (
                                <IconButton
                                    Icon={IconControlledSubstance}
                                    withStyles={false}
                                    onClick={
                                        helpCenterMedicationEnabled
                                            ? () =>
                                                  navigate(
                                                      getAbsoluteRoute(
                                                          'helpCenter.medications'
                                                      )
                                                  )
                                            : undefined
                                    }
                                />
                            ) : null}
                        </Flex>
                    </Stack>
                    <ConditionalComponent condition={shouldShowOrderDetails}>
                        <PrescriberOrderDetails
                            showRxImage
                            showQuantity
                            prescriberOrder={prescriberOrder}
                            priceQuote={priceQuote}
                            showPrescriber={false}
                        />
                    </ConditionalComponent>
                    {prescriberOrder.applicablePriorAuthorization ? (
                        <PriorAuthLastActivityCard
                            priorAuthorization={getFragment(
                                prescriberOrder.applicablePriorAuthorization
                            )}
                            prescriberOrderId={prescriptionId}
                            pharmacyProvider={pharmacyProvider}
                        />
                    ) : (
                        <LastOrderActivity
                            prescriberOrder={prescriberOrder}
                            priceQuote={priceQuote}
                        />
                    )}
                </Stack>
                <Stack gap="sm">
                    <Heading2>
                        {t('orderConfirmation.prescriber.subHeader')}
                    </Heading2>
                    <Prescriber
                        prescriber={getFragment(prescriberOrder.prescriber)}
                    />
                </Stack>
            </Stack>
            <ConditionalComponent condition={showFillButton}>
                <Button
                    onClick={() =>
                        navigate(
                            getAbsoluteRoute('cabinet.rxId.root', {
                                prescriptionId,
                            })
                        )
                    }
                >
                    {t('orderDetails.fillPrescription')}
                </Button>
            </ConditionalComponent>
        </Stack>
    );
};
